import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Trash2, Check, RefreshCw, Calendar, Users, Clock, Mail, Phone, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TKEY = "sip_admin_token";

const TOPIC = {
  booking: { label: "Table booking", cls: "bg-pine/10 text-pine border-pine/30" },
  meeting: { label: "Meeting room", cls: "bg-gold/15 text-golddeep border-gold/40" },
  catering: { label: "Catering", cls: "bg-heart/10 text-heart border-heart/30" },
  feedback: { label: "Feedback", cls: "bg-smoke/10 text-smoke border-line" },
  general: { label: "Message", cls: "bg-smoke/10 text-smoke border-line" },
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "booking", label: "Bookings" },
  { key: "meeting", label: "Meeting room" },
  { key: "messages", label: "Messages" },
];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TKEY) || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async (tk) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/contact`, { headers: { Authorization: `Bearer ${tk}` } });
      setItems(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TKEY);
        setToken("");
        toast.error("Session expired — please sign in again.");
      } else {
        toast.error("Failed to load inbox.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      toast.dismiss();
      localStorage.setItem(TKEY, data.token);
      setToken(data.token);
      setPassword("");
    } catch (err) {
      const s = err?.response?.status;
      toast.error(s === 401 ? "Incorrect password" : s === 429 ? "Too many attempts — try again in a few minutes." : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TKEY);
    setToken("");
    setItems([]);
  };

  const handleAuthError = (e, fallback) => {
    if (e?.response?.status === 401) {
      localStorage.removeItem(TKEY);
      setToken("");
      toast.error("Session expired — please sign in again.");
    } else {
      toast.error(fallback);
    }
  };

  const toggleHandled = async (m) => {
    try {
      await axios.patch(`${API}/contact/${m.id}`, { handled: !m.handled }, authHeader);
      setItems((x) => x.map((i) => (i.id === m.id ? { ...i, handled: !i.handled } : i)));
    } catch (e) {
      handleAuthError(e, "Update failed");
    }
  };

  const remove = async (m) => {
    if (!window.confirm("Delete this entry permanently?")) return;
    try {
      await axios.delete(`${API}/contact/${m.id}`, authHeader);
      setItems((x) => x.filter((i) => i.id !== m.id));
      toast.success("Deleted");
    } catch (e) {
      handleAuthError(e, "Delete failed");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-forest flex items-center justify-center px-6" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-sm bg-paper p-10 border border-line">
          <div className="flex items-center gap-3 mb-8">
            <Lock size={18} className="text-gold" />
            <h1 className="font-cinzel text-xl text-pine tracking-wide">Sipsation Admin</h1>
          </div>
          <label className="text-xs uppercase tracking-[0.25em] text-smoke">Admin password</label>
          <input
            type="password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            data-testid="admin-password-input"
            className="w-full bg-transparent border-b border-line focus:border-gold outline-none py-3 mt-2 mb-8 text-ink font-body"
          />
          <button
            type="submit"
            disabled={loading || !password}
            data-testid="admin-login-button"
            className="w-full bg-pine text-paper text-xs uppercase tracking-[0.2em] py-4 hover:bg-forest disabled:opacity-50 transition-colors duration-300"
          >
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>
      </div>
    );
  }

  const counts = {
    all: items.length,
    booking: items.filter((i) => i.topic === "booking").length,
    meeting: items.filter((i) => i.topic === "meeting").length,
    messages: items.filter((i) => !["booking", "meeting"].includes(i.topic)).length,
  };
  const shown = items.filter((i) =>
    filter === "all" ? true : filter === "messages" ? !["booking", "meeting"].includes(i.topic) : i.topic === filter
  );

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-dashboard">
      <header className="bg-forest text-paper px-6 md:px-10 py-6 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Inbox size={18} className="text-gold" />
          <h1 className="font-cinzel text-lg tracking-wide">Sipsation Inbox</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => load(token)} data-testid="admin-refresh" className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-paper/80 border border-paper/20 px-4 py-2.5 hover:border-gold hover:text-gold transition-colors">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-paper/80 border border-paper/20 px-4 py-2.5 hover:border-heart hover:text-heart transition-colors">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <div className="px-6 md:px-10 py-8 max-w-[1200px] mx-auto">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`admin-filter-${f.key}`}
              className={`text-xs uppercase tracking-[0.15em] px-4 py-2.5 border transition-colors ${
                filter === f.key ? "bg-pine text-paper border-pine" : "bg-transparent text-smoke border-line hover:border-gold hover:text-gold"
              }`}
            >
              {f.label} <span className="opacity-60">({counts[f.key]})</span>
            </button>
          ))}
        </div>

        {shown.length === 0 ? (
          <p className="text-smoke text-sm py-20 text-center" data-testid="admin-empty">
            {loading ? "Loading..." : "Nothing here yet."}
          </p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-list">
            {shown.map((m) => {
              const tp = TOPIC[m.topic] || TOPIC.general;
              return (
                <article
                  key={m.id}
                  data-testid={`admin-card-${m.id}`}
                  className={`border p-6 flex flex-col ${m.handled ? "bg-parchment/50 border-line opacity-70" : "bg-parchment border-line"}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-4">
                    <span className={`text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 border ${tp.cls}`}>{tp.label}</span>
                    <span className="text-[10px] text-smoke">{fmtDate(m.created_at)}</span>
                  </div>
                  <h3 className="font-display text-2xl text-ink leading-tight">{m.name}</h3>
                  <div className="mt-2 space-y-1 text-sm text-smoke">
                    <p className="flex items-center gap-2"><Phone size={13} className="text-gold" /> {m.contact}</p>
                    {m.email && <p className="flex items-center gap-2 break-all"><Mail size={13} className="text-gold" /> {m.email}</p>}
                  </div>
                  {(m.date || m.time) && (
                    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-pine">
                      {m.date && <span className="flex items-center gap-1.5"><Calendar size={13} className="text-gold" /> {m.date}</span>}
                      {m.time && <span className="flex items-center gap-1.5"><Clock size={13} className="text-gold" /> {m.time}</span>}
                      {m.party_size && <span className="flex items-center gap-1.5"><Users size={13} className="text-gold" /> {m.party_size}</span>}
                      {m.duration && <span className="text-golddeep">{m.duration}</span>}
                    </div>
                  )}
                  {m.purpose && <p className="mt-2 text-sm text-ink/80"><span className="text-smoke">Purpose:</span> {m.purpose}</p>}
                  {m.message && <p className="mt-3 text-sm text-ink/75 leading-relaxed border-t border-line pt-3">{m.message}</p>}
                  <div className="mt-5 pt-4 border-t border-line flex items-center gap-2">
                    <button
                      onClick={() => toggleHandled(m)}
                      data-testid={`admin-handle-${m.id}`}
                      className={`flex-1 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.15em] py-2.5 border transition-colors ${
                        m.handled ? "border-line text-smoke hover:border-gold hover:text-gold" : "bg-pine text-paper border-pine hover:bg-forest"
                      }`}
                    >
                      <Check size={13} /> {m.handled ? "Handled" : "Mark handled"}
                    </button>
                    <button
                      onClick={() => remove(m)}
                      data-testid={`admin-delete-${m.id}`}
                      className="p-2.5 border border-line text-smoke hover:border-heart hover:text-heart transition-colors"
                      aria-label="Delete"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
